<template>
  <SideBar :class="{ 'disable-last-pane-resize': !expandedNews }">
    <Splitpanes class="default-theme" horizontal @resize="handleResize">
      <Pane
        :size="panesSize.watchList.size"
        :min-size="panesSize.watchList.minSize"
      >
        <Watchlist :is-expanded-news="expandedNews" />
      </Pane>

      <Pane
        v-if="showTechnicalChart && !showPortfolioVisualization"
        :size="panesSize.symbol.size"
        :min-size="panesSize.symbol.minSize"
      >
        <ComponentState :fetch-status="symbolStatus">
          <template #loading>
            <SymbolVerticalSkeleton />
          </template>

          <template #error>
            <Error class="p-2" @retry="() => getSymbol()" />
          </template>

          <SymbolDetail
            vertical
            :symbol="symbol"
            :portfolio-symbols="portfolioSymbols"
          />
        </ComponentState>
      </Pane>

      <Pane :size="panesSize.news.size" :min-size="panesSize.news.minSize">
        <News v-model:expanded-news="expandedNews" />
      </Pane>
    </Splitpanes>
  </SideBar>

  <Main
    :class="{ 'disable-last-pane-resize': !expandedAssets }"
    class="overflow-x-auto"
  >
    <Splitpanes
      ref="container"
      class="default-theme"
      horizontal
      @resize="handleResizeLeft"
    >
      <Pane
        v-bind="showTechnicalChart ? panesSize.technical : panesSize.symbol"
        :class="{ '!h-0': showPortfolioVisualization }"
      >
        <Symbol
          :portfolio-symbols="portfolioSymbols"
          :symbol="symbol"
          :symbol-status="symbolStatus"
          :isin="activeIsin"
          @handle-show-technical-chart="handleShowTechnicalChart"
          @get-symbol="getSymbol"
        />
      </Pane>

      <Pane
        v-bind="panesSize.assets"
        :class="{ '!h-full': showPortfolioVisualization }"
      >
        <div
          v-if="showPortfolioVisualization"
          class="flex rounded-md bg-neutral-secondary px-2 h-full mx-1"
        >
          <PortfolioVisualization
            :portfolio-symbols="portfolioSymbols"
            @handle-portfolio-visualization="handlePortfolioVisualization"
          />
        </div>

        <div
          v-else
          class="flex rounded-md bg-neutral-secondary px-2 h-full mx-1"
        >
          <div
            class="flex items-center justify-between bg-neutral-secondary py-2 w-full relative"
          >
            <NTabs v-model="tab">
              <NTab
                v-gid="{
                  module: 'portfolio',
                  screen: 'home',
                  section: 'assetsTabSection',
                  component: 'portfolioTabButton',
                }"
                rounded="full"
                :title="t('modules.portfolio.title')"
                class="!bg-neutral-primary hover:!bg-neutral-primary-hover"
                @click="() => updateExpandedAssetsSection(true)"
              >
                <PortfolioTable
                  v-if="expandedAssets"
                  :portfolio-symbols="portfolioSymbols"
                  :portfolio-status="portfolioStatus"
                />
              </NTab>

              <NPopper hover offset-y="5" placement="top" append-to-body>
                <div
                  v-gid="{
                    module: 'portfolio',
                    screen: 'home',
                    section: 'assetsTabSection',
                    component: 'showPortfolioVisualizationButton',
                  }"
                  class="flex items-center cursor-pointer justify-center bg-neutral-secondary hover:bg-neutral-secondary hover:text-neutral-secondary-press rounded-full w-8 h-8"
                  @click.stop="handlePortfolioVisualization"
                >
                  <span class="w-8">
                    <NegarExpandIcon class="text-neutral-secondary mx-auto" />
                  </span>
                </div>

                <template #content>
                  <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                    <NText
                      class="text-neutral-quaternary"
                      variant="caption2"
                      weight="medium"
                    >
                      {{ t('modules.portfolio.portfolioStatus') }}
                    </NText>
                  </div>
                </template>
              </NPopper>

              <div
                v-if="embeddedPutOptionSymbols.length"
                class="flex items-center justify-center mr-10 -ml-10"
              >
                <NTab
                  v-gid="{
                    module: 'portfolio',
                    screen: 'home',
                    section: 'assetsTabSection',
                    component: 'embeddedPutOptionTabButton',
                  }"
                  rounded="full"
                  :title="t('modules.portfolio.embeddedPutOption.title')"
                  class="flex-none"
                  @click.stop
                >
                  <EmbeddedPutOptionTable :symbols="embeddedPutOptionSymbols" />
                </NTab>
              </div>

              <div
                class="flex items-center justify-center xl:mr-20 mr-16 border-r border-neutral-tertiary"
              >
                <NText
                  variant="caption1"
                  weight="bold"
                  class="text-neutral-secondary pl-2 mr-2 xl:mr-10"
                >
                  {{ t('common.orders') }}:
                </NText>
              </div>

              <OrdersTableTab
                v-for="({ label, value }, index) in Object.values(
                  ORDER_FILTERS
                )"
                :key="value"
                :title="label"
                :is-active="isActiveOrderTab(index)"
                :filter="value"
                :orders="orders"
                :is-error-loading-orders="isErrorLoadingOrders"
                :expanded-assets="expandedAssets"
                @click="() => updateExpandedAssetsSection(true)"
                @get-orders="getOrders"
              />

              <div
                class="flex items-center justify-center xl:pr-10 xl:mr-8 pr-2 border-r border-neutral-tertiary relative"
              >
                <NTab
                  rounded="full"
                  class="!bg-neutral-primary hover:!bg-neutral-primary-hover"
                  :title="t('orders.trade')"
                  @click.stop
                >
                  <template v-if="hasNewSummarize" #append>
                    <span
                      class="bg-critical-primary w-2 h-2 rounded-full absolute top-0 left-0"
                    />
                  </template>

                  <ComponentState :fetch-status="orderSummarizeStatus">
                    <template #loading>
                      <TableSkeleton :row-number="10" :column-number="8" />
                    </template>

                    <template #error>
                      <Error @retry="getOrderSummarize" />
                    </template>

                    <SummarizeOrdersTable :items="summarizedOrders" />
                  </ComponentState>
                </NTab>
              </div>
            </NTabs>

            <NegarMinusIcon
              v-if="expandedAssets"
              class="inline transition duration-300 absolute left-2 top-3 cursor-pointer"
              @click="() => updateExpandedAssetsSection()"
            />

            <NegarArrowUpIcon
              v-else
              class="inline transition duration-300 absolute left-2 top-3 cursor-pointer"
              @click="() => updateExpandedAssetsSection()"
            />
          </div>
        </div>
      </Pane>
    </Splitpanes>
  </Main>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Splitpanes, Pane } from 'splitpanes';
import {
  useGetEmbeddedPutOptionSymbolsQuery,
  useLivePortfolioQuery,
} from '@core/portfolio';
import { pxToPercent } from '@libs/utils';
import { queryStatus } from '@libs/use';
import {
  ORDER_FILTERS,
  useLiveOrdersQuery,
  useLiveOrderSummarizeQuery,
} from '@core/order';
import {
  NegarArrowUpIcon,
  NegarMinusIcon,
  NegarExpandIcon,
} from '@negar/icons';
import { useRoute, useRouter } from 'vue-router';
import { IPO_CONFIG } from '@core/ipo'; // todo GTM problem
import {
  getSymbolIsin,
  useSyncedSymbolDetailInBrowserTitle,
} from '@core/symbol';
import { watchOnce } from '@vueuse/core';

import { Watchlist } from '@/modules/watchlist';
import {
  SymbolDetail,
  Symbol,
  SALAM_ISIN,
  SymbolVerticalSkeleton,
} from '@/modules/symbol';
import { News } from '@/modules/news';
import {
  OrdersTableTab,
  SummarizeOrdersTable,
  useOrderShortcuts,
} from '@/modules/order';
import { useUserSettings } from '@/modules/user';
import {
  PortfolioTable,
  PortfolioVisualization,
  EmbeddedPutOptionTable,
} from '@/modules/portfolio';
import { useActiveSymbolIsin, useLiveSymbolQuery } from '@/modules/symbol';

import SideBar from '@/components/SideBar.vue';
import Main from '@/components/Main.vue';
import Error from '@/components/Error.vue';
import TableSkeleton from '@/components/TableSkeleton.vue';
import ComponentState from '@/components/ComponentState.vue';

import usePaneResize from '@/composables/usePaneResize';

const PADDING_FOR_PANES_SIZE = 3;

const ipoConfig = ref(IPO_CONFIG); // todo GTM problem
const showTechnicalChart = ref(false);
const tab = ref(0);
const SUMMARIZE_ORDER_TAB_VALUE = 5;

const { settings, updateLayoutSettings } = useUserSettings();

const expandedAssets = ref(settings.value.layout?.expandedAssets ?? true);
const expandedNews = ref(settings.value.layout?.expandedNews ?? true);

const showPortfolioVisualization = ref(false);
const container = ref();

const router = useRouter();
const route = useRoute();

const bottomSectionMinSize = computed(() =>
  pxToPercent(container.value?.$el?.clientHeight, 52)
);

const { activeIsin, updateActiveIsin } = useActiveSymbolIsin();

const {
  symbols: portfolioSymbols,
  isSuccessLoadingPortfolio,
  getPortfolio,
  isFetchingPortfolio,
  isErrorLoadingPortfolio,
  isRevalidatingSymbols,
  isLoadingSymbols,
  isSymbolLoaded,
} = useLivePortfolioQuery();

const portfolioStatus = computed(() => {
  if (isErrorLoadingPortfolio.value) return queryStatus.error;
  if (isSymbolLoaded.value) return queryStatus.success;

  return queryStatus.loading;
});

useOrderShortcuts(activeIsin);

const { orders, getOrders, isErrorLoadingOrders } = useLiveOrdersQuery();

const {
  summarizedOrders,
  getOrderSummarize,
  hasNewSummarize,
  orderSummarizeStatus,
} = useLiveOrderSummarizeQuery({
  immediate: computed(() => tab.value === SUMMARIZE_ORDER_TAB_VALUE),
});

const { symbol, getSymbol, symbolStatus } = useLiveSymbolQuery(activeIsin);

useSyncedSymbolDetailInBrowserTitle(symbol);

const { embeddedPutOptionSymbols } = useGetEmbeddedPutOptionSymbolsQuery();

const { panesSize, handleResize, handleResizeLeft } = usePaneResize({
  bottomSectionMinSize,
  expandedNews,
  expandedAssets,
  showTechnicalChart,
});

const { t } = useI18n();

const isActiveOrderTab = (index: number) => tab.value === index + 1;

const handleShowTechnicalChart = (newValue: boolean, isExpanded: boolean) => {
  showTechnicalChart.value = newValue;

  expandedNews.value = settings.value.layout.expandedNews ?? !newValue;
  expandedAssets.value = settings.value.layout.expandedAssets ?? !isExpanded;
};

const handlePortfolioVisualization = () => {
  showPortfolioVisualization.value = !showPortfolioVisualization.value;

  if (showPortfolioVisualization.value) {
    expandedNews.value = true;

    router.push({ query: { ...route.query, portfolio: 'true' } });
  } else {
    const newQuery = { ...route.query };

    expandedNews.value = settings.value.layout.expandedNews ?? true;

    delete newQuery.portfolio;
    delete newQuery.virtualPortfolio;

    router.push({ query: newQuery });
  }
};

function handleActiveSummarizeTab() {
  getOrderSummarize();

  hasNewSummarize.value = false;
}

const activeTabAction = computed(() => {
  const activeTabIndex = tab.value;

  if (activeTabIndex === 0) return getPortfolio;

  if (activeTabIndex > 0 && activeTabIndex < 5) return getOrders;

  if (activeTabIndex === 5) return handleActiveSummarizeTab;

  return () => {};
});

watch(activeTabAction, (callAction) => callAction());

watch(
  () => panesSize.assets.size,
  () => {
    if (
      panesSize.assets.size <
      panesSize.assets.minSize + PADDING_FOR_PANES_SIZE
    ) {
      expandedAssets.value = false;
    } else {
      expandedAssets.value = true;
    }
  }
);

watch(
  () => panesSize.news.size,
  () => {
    if (
      panesSize.news.size <=
      panesSize.news.minSize + PADDING_FOR_PANES_SIZE
    ) {
      expandedNews.value = false;
    } else {
      expandedNews.value = true;
    }
  }
);

const updateExpandedAssetsSection = (value?: boolean) => {
  updateLayoutSettings('expandedAssets', value ?? !expandedAssets.value);

  expandedAssets.value = value ?? !expandedAssets.value;
};

watchOnce(
  () => route,
  () => {
    if (route.query.portfolio === 'true' || route.query.virtualPortfolio) {
      showPortfolioVisualization.value = true;
    }
  },
  {
    immediate: true,
  }
);

const unwatchPortfolioSymbols = watch(isFetchingPortfolio, async () => {
  const firstSymbol = portfolioSymbols.value?.sort(
    (a, b) => b.value - a.value
  )?.[0];

  if (
    Boolean(portfolioSymbols.value.length) &&
    firstSymbol &&
    !route.query.isin
  ) {
    await updateActiveIsin(getSymbolIsin(firstSymbol));

    unwatchPortfolioSymbols();
  } else if (
    (isSuccessLoadingPortfolio.value || isErrorLoadingPortfolio.value) &&
    !route.query.isin
  ) {
    await updateActiveIsin(SALAM_ISIN);

    unwatchPortfolioSymbols();
  }
});
</script>

<route lang="yaml">
meta:
  auth: true
  layout: App
  title: common.worthyDocuments
</route>

<style>
.splitpanes.default-theme .splitpanes__pane,
.splitpanes.default-theme .splitpanes__splitter {
  background-color: inherit !important;
}

.splitpanes--vertical > .splitpanes__splitter {
  width: 8px !important;
}

.splitpanes--horizontal > .splitpanes__splitter {
  height: 8px !important;
  margin-top: 0 !important;
}

.splitpanes__splitter {
  border: none !important;
}

.splitpanes__splitter:before,
.splitpanes__splitter:after {
  width: 20px !important;
  background-color: #999999 !important;
}

.disable-last-pane-resize .splitpanes__splitter:nth-last-child(2) {
  pointer-events: none;
}
</style>
